import {
  createAuthClient as createKeycloakAuthClient,
  type AuthClient,
  type AuthIdTokenParsed,
  type AuthOptions,
} from '@storebrand-digital/auth';
import type { Env } from '@bank/utils/createGetEnv';

export type BankAuthClient = Pick<
  AuthClient,
  | 'init'
  | 'login'
  | 'logout'
  | 'getToken'
  | 'authenticated'
  | 'authenticatedOnBehalfOf'
  | 'idTokenParsed'
  | 'nin'
>;

// Fake identity exposed by the offline client, so E2E screens have a name to show.
const OFFLINE_IDENTITY: AuthIdTokenParsed = {
  sub: 'e2e-user',
  cm_id: 'e2e',
  no_nin: '01010112345',
  given_name: 'E2E',
  family_name: 'Test',
  name: 'E2E Test',
  jti: 'e2e',
  at_hash: 'e2e',
  locale: 'no',
  preferred_username: 'e2e-user',
  typ: 'ID',
  // Highest assurance level (AL4), so AL3/AL4-gated calls (overview2 settlements) still run.
  acr: 'https://id.storebrand.no/authn/al4',
  iat: 0,
  exp: Number.MAX_SAFE_INTEGER,
};

const offlineAuthClient: BankAuthClient = {
  init: () => Promise.resolve(true),
  login: () => Promise.resolve(),
  logout: () => Promise.resolve(),
  getToken: () => Promise.resolve(''),
  authenticated: true,
  // Not acting on behalf of anyone. An app guarding on this (kyc-dashboard) needs its own offline value.
  authenticatedOnBehalfOf: null,
  idTokenParsed: OFFLINE_IDENTITY,
  nin: OFFLINE_IDENTITY.no_nin,
};

/**
 * Build the app's auth client for the given environment.
 *
 * Every environment but `offline` gets the real Keycloak client. `offline` (an
 * E2E build, see `createGetEnv`) gets a client that is always authenticated and
 * never contacts Keycloak. Its token is empty, so a real backend still rejects
 * its requests.
 *
 * @example
 * ```ts
 * export const auth = createAuthClient(options, getEnv());
 * ```
 */
export const createAuthClient = (
  options: AuthOptions,
  env: Env,
): BankAuthClient => {
  if (env === 'offline') {
    return offlineAuthClient;
  }
  return createKeycloakAuthClient(options);
};
